import React, { useState, useEffect, useMemo } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import DatePicker from "react-datepicker";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import { fr } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import {
  Badge,
  Modal,
  ModalBody,
  Spinner,
  Input,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Row,
  Col,
  PreviewCard,
  PreviewAltCard,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
} from "../../../components/Component";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";

// Enregistrer la locale française
registerLocale("fr", fr);
setDefaultLocale("fr");

const CardOfMonthPage = () => {
  const [topCards, setTopCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewModal, setViewModal] = useState(false);
  const [selectedObservation, setSelectedObservation] = useState(null);
  const [employeeOfMonth, setEmployeeOfMonth] = useState(null);
  const [selectingCard, setSelectingCard] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [observationToSelect, setObservationToSelect] = useState(null);

  const selectedMonth = selectedDate.getMonth() + 1;
  const selectedYear = selectedDate.getFullYear();

  useEffect(() => {
    loadTopCards();
    loadEmployeeOfMonth();
  }, [selectedMonth, selectedYear]);

  const loadTopCards = async () => {
    setLoading(true);
    try {
      const response = await api.getCardOfMonth({
        month: selectedMonth,
        year: selectedYear,
      });
      
      if (response.top10) {
        setTopCards(response.top10);
        setSelectedCard(response.selected_card);
      } else {
        setTopCards([]);
        setSelectedCard(null);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des cartes du mois");
      setTopCards([]);
      setSelectedCard(null);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeOfMonth = async () => {
    try {
      const response = await api.getEmployeeOfMonth({
        month: selectedMonth,
        year: selectedYear,
      });
      setEmployeeOfMonth(response);
    } catch (error) {
      // Ignorer l'erreur silencieusement
      setEmployeeOfMonth(null);
    }
  };

  const handleSelectCard = (observationId) => {
    const observation = topCards.find((card) => card.id === observationId);
    setObservationToSelect(observation);
    setConfirmModal(true);
  };

  const onConfirmSelect = async () => {
    if (!observationToSelect) return;

    setSelectingCard(true);
    try {
      await api.selectCardOfMonth(observationToSelect.id);
      toast.success("Carte du mois sélectionnée avec succès !");
      setConfirmModal(false);
      setObservationToSelect(null);
      loadTopCards();
    } catch (error) {
      toast.error("Erreur lors de la sélection de la carte du mois");
    } finally {
      setSelectingCard(false);
    }
  };

  const onCancelSelect = () => {
    setConfirmModal(false);
    setObservationToSelect(null);
  };

  const openViewModal = (observation) => {
    setSelectedObservation(observation);
    setViewModal(true);
  };

  const getTypeBadge = (type) => {
    const badges = {
      positive: { color: "success", label: "Positive" },
      dangerous: { color: "danger", label: "Dangereuse" },
      near_miss: { color: "warning", label: "Presqu'accident" },
      non_conformity: { color: "info", label: "Non-conformité" },
    };
    const badge = badges[type] || { color: "secondary", label: type };
    return <Badge color={badge.color}>{badge.label}</Badge>;
  };

  const getSeverityBadge = (severity) => {
    const badges = {
      high: { color: "danger", label: "Élevée" },
      medium: { color: "warning", label: "Moyenne" },
      low: { color: "info", label: "Faible" },
    };
    const badge = badges[severity] || { color: "secondary", label: severity };
    return <Badge color={badge.color}>{badge.label}</Badge>;
  };

  const months = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
  ];

  const handleDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  return (
    <React.Fragment>
      <Head title="Carte du mois" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Carte du mois</BlockTitle>
              <BlockDes className="text-soft">
                Top 10 des meilleures observations du mois
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="d-flex align-items-center gap-2">
                <Icon name="calendar" className="d-none d-sm-inline"></Icon>
                <div style={{ minWidth: "200px" }}>
                  <DatePicker
                    selected={selectedDate}
                    onChange={handleDateChange}
                    dateFormat="MMMM yyyy"
                    showMonthYearPicker
                    className="form-control"
                    wrapperClassName="date-picker"
                    maxDate={new Date()}
                    locale="fr"
                  />
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Row className="g-gs">
            {/* Carte sélectionnée */}
            {selectedCard && (
              <Col lg="12">
                <PreviewAltCard className="card-full">
                  <div className="card-inner">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h5 className="title">
                          <Icon name="star" className="text-warning me-2"></Icon>
                          Carte du mois sélectionnée
                        </h5>
                        <p className="text-soft">Observation choisie par le Responsable HSE</p>
                      </div>
                      <Badge color="warning">Sélectionnée</Badge>
                    </div>
                    <div className="card-details">
                      <h6 className="mb-2">{selectedCard.title}</h6>
                      <p className="text-soft mb-3">{selectedCard.description?.substring(0, 200)}...</p>
                      <div className="d-flex gap-2 flex-wrap mb-3">
                        {getTypeBadge(selectedCard.type)}
                        {getSeverityBadge(selectedCard.severity)}
                        <Badge color="success">
                          Score: {selectedCard.month_score ? parseFloat(selectedCard.month_score).toFixed(1) : "N/A"}
                        </Badge>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <span className="text-soft">Par: </span>
                          <span>
                            {selectedCard.creator
                              ? `${selectedCard.creator.first_name || ""} ${selectedCard.creator.last_name || ""}`.trim() || selectedCard.creator.email
                              : "N/A"}
                          </span>
                          {selectedCard.site && (
                            <>
                              <span className="text-soft ms-3">Site: </span>
                              <span>{selectedCard.site.name}</span>
                            </>
                          )}
                        </div>
                        <Button
                          color="light"
                          size="sm"
                          onClick={() => openViewModal(selectedCard)}
                        >
                          <Icon name="eye"></Icon>
                          <span>Voir les détails</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </PreviewAltCard>
              </Col>
            )}

            {/* Employé du mois */}
            {employeeOfMonth && employeeOfMonth.user && (
              <Col lg="12">
                <PreviewAltCard className="card-full">
                  <div className="card-inner" style={{ background: "linear-gradient(135deg, #198754 0%, #146c43 100%)", borderRadius: "12px", padding: "2rem", color: "white" }}>
                    <div className="d-flex justify-content-between align-items-start mb-4">
                      <div>
                        <div className="d-flex align-items-center mb-2">
                          <div style={{ 
                            width: "60px", 
                            height: "60px", 
                            borderRadius: "50%", 
                            background: "rgba(25, 135, 84, 0.3)", 
                            display: "flex", 
                            alignItems: "center", 
                            justifyContent: "center",
                            marginRight: "1rem",
                            border: "2px solid rgba(25, 135, 84, 0.4)"
                          }}>
                            <Icon name="user" style={{ fontSize: "28px", color: "white" }}></Icon>
                          </div>
                          <div>
                            <h5 className="mb-1" style={{ color: "white", fontWeight: "600" }}>
                              Employé du mois
                            </h5>
                            <p className="mb-0" style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "0.95rem" }}>
                              {employeeOfMonth.user.first_name} {employeeOfMonth.user.last_name}
                            </p>
                            {employeeOfMonth.user.email && (
                              <p className="mb-0" style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "0.85rem" }}>
                                {employeeOfMonth.user.email}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        background: "rgba(255, 255, 255, 0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "3px solid rgba(255, 255, 255, 0.3)"
                      }}>
                        <Icon name="award" style={{ fontSize: "40px", color: "#FFD700" }}></Icon>
                      </div>
                    </div>
                    <div className="row g-3 mt-3">
                      <div className="col-md-6">
                        <div style={{
                          background: "rgba(255, 255, 255, 0.15)",
                          borderRadius: "10px",
                          padding: "1.25rem",
                          backdropFilter: "blur(10px)"
                        }}>
                          <div className="d-flex align-items-center">
                            <div style={{
                              width: "48px",
                              height: "48px",
                              borderRadius: "10px",
                              background: "rgba(25, 135, 84, 0.3)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginRight: "1rem"
                            }}>
                              <Icon name="file-text" style={{ fontSize: "24px", color: "white" }}></Icon>
                            </div>
                            <div>
                              <p className="mb-0" style={{ color: "rgba(255, 255, 255, 0.8)", fontSize: "0.875rem" }}>
                                Observations
                              </p>
                              <h4 className="mb-0" style={{ color: "white", fontWeight: "700" }}>
                                {employeeOfMonth.observation_count}
                              </h4>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div style={{
                          background: "rgba(255, 255, 255, 0.15)",
                          borderRadius: "10px",
                          padding: "1.25rem",
                          backdropFilter: "blur(10px)"
                        }}>
                          <div className="d-flex align-items-center">
                            <div style={{
                              width: "48px",
                              height: "48px",
                              borderRadius: "10px",
                              background: "rgba(25, 135, 84, 0.3)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginRight: "1rem"
                            }}>
                              <Icon name="star" style={{ fontSize: "24px", color: "#FFD700" }}></Icon>
                            </div>
                            <div>
                              <p className="mb-0" style={{ color: "rgba(255, 255, 255, 0.8)", fontSize: "0.875rem" }}>
                                Score moyen
                              </p>
                              <h4 className="mb-0" style={{ color: "white", fontWeight: "700" }}>
                                {employeeOfMonth.avg_score ? parseFloat(employeeOfMonth.avg_score).toFixed(1) : "N/A"}
                              </h4>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </PreviewAltCard>
              </Col>
            )}

            {/* Top 10 */}
            <Col lg="12">
              <PreviewCard>
                <div className="nk-block-head">
                  <div className="nk-block-between">
                    <div className="nk-block-head-content">
                      <h5 className="title">Classement des observations</h5>
                      <p className="text-soft">Top 10 des meilleures observations du mois</p>
                    </div>
                  </div>
                </div>
                {loading ? (
                  <div className="text-center p-4">
                    <Spinner color="primary" />
                  </div>
                ) : topCards.length === 0 ? (
                  <div className="text-center p-4">
                    <span className="text-soft">Aucune observation disponible pour ce mois</span>
                  </div>
                ) : (
                  <DataTable className="card-stretch">
                    <DataTableBody>
                      <DataTableHead className="nk-tb-item nk-tb-head">
                        <DataTableRow className="text-center">
                          <span className="sub-text">Rang</span>
                        </DataTableRow>
                        <DataTableRow size="xxl">
                          <span className="sub-text">Observation</span>
                        </DataTableRow>
                        <DataTableRow size="xl">
                          <span className="sub-text">Type</span>
                        </DataTableRow>
                        <DataTableRow size="xl">
                          <span className="sub-text">Gravité</span>
                        </DataTableRow>
                        <DataTableRow size="md">
                          <span className="sub-text">Score</span>
                        </DataTableRow>
                        <DataTableRow size="md">
                          <span className="sub-text">Auteur</span>
                        </DataTableRow>
                        <DataTableRow className="nk-tb-col-tools text-end">
                          <span className="sub-text">Actions</span>
                        </DataTableRow>
                      </DataTableHead>
                      {topCards.map((card, index) => (
                        <DataTableItem key={card.id}>
                          <DataTableRow className="text-center">
                            <div className="d-flex align-items-center justify-content-center">
                              {index === 0 && <Icon name="trophy" className="text-warning me-2"></Icon>}
                              <Badge color={index === 0 ? "warning" : index < 3 ? "success" : "secondary"}>
                                #{index + 1}
                              </Badge>
                            </div>
                          </DataTableRow>
                          <DataTableRow size="xxl">
                            <div>
                              <h6 className="title" style={{ maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={card.title}>
                                {card.title}
                              </h6>
                              {card.site && (
                                <span className="sub-text">{card.site.name}</span>
                              )}
                            </div>
                          </DataTableRow>
                          <DataTableRow size="xl">
                            {getTypeBadge(card.type)}
                          </DataTableRow>
                          <DataTableRow size="xl">
                            {getSeverityBadge(card.severity)}
                          </DataTableRow>
                          <DataTableRow size="md">
                            <Badge color="success">
                              {card.month_score ? parseFloat(card.month_score).toFixed(1) : "N/A"}
                            </Badge>
                          </DataTableRow>
                          <DataTableRow size="md">
                            <span>
                              {card.creator
                                ? `${card.creator.first_name || ""} ${card.creator.last_name || ""}`.trim() || card.creator.email
                                : "N/A"}
                            </span>
                          </DataTableRow>
                          <DataTableRow className="nk-tb-col-tools text-end">
                            <ul className="nk-tb-actions gx-1">
                              <li>
                                <Button
                                  color="light"
                                  size="sm"
                                  onClick={() => openViewModal(card)}
                                  className="btn-icon"
                                  title="Voir les détails"
                                >
                                  <Icon name="eye"></Icon>
                                </Button>
                              </li>
                              {!card.is_card_of_month && (
                                <li>
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => handleSelectCard(card.id)}
                                    className="btn-icon"
                                    title="Sélectionner comme carte du mois"
                                    disabled={selectingCard}
                                  >
                                    <Icon name="star"></Icon>
                                  </Button>
                                </li>
                              )}
                              {card.is_card_of_month && (
                                <li>
                                  <Badge color="warning">Sélectionnée</Badge>
                                </li>
                              )}
                            </ul>
                          </DataTableRow>
                        </DataTableItem>
                      ))}
                    </DataTableBody>
                  </DataTable>
                )}
              </PreviewCard>
            </Col>
          </Row>
        </Block>

        {/* Modal pour voir les détails */}
        <Modal isOpen={viewModal} toggle={() => setViewModal(false)} className="modal-dialog-centered modal-lg">
          <div className="modal-header">
            <h5 className="modal-title">Détails de l'observation</h5>
            <button className="close" onClick={() => setViewModal(false)}>
              <Icon name="cross"></Icon>
            </button>
          </div>
          <ModalBody>
            {selectedObservation && (
              <div className="nk-modal-details">
                <div className="nk-block nk-block-md">
                  <h6 className="text-soft mb-3">
                    <Icon name="info" className="me-2"></Icon>
                    Informations principales
                  </h6>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="form-group">
                        <label className="form-label">Titre</label>
                        <div className="form-control-plaintext">
                          {selectedObservation.title}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Type</label>
                        <div className="form-control-plaintext">
                          {getTypeBadge(selectedObservation.type)}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Gravité</label>
                        <div className="form-control-plaintext">
                          {getSeverityBadge(selectedObservation.severity)}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Score du mois</label>
                        <div className="form-control-plaintext">
                          <Badge color="success">
                            {selectedObservation.month_score ? parseFloat(selectedObservation.month_score).toFixed(1) : "N/A"}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Score qualité</label>
                        <div className="form-control-plaintext">
                          <Badge color="info">
                            {selectedObservation.quality_score ? parseFloat(selectedObservation.quality_score).toFixed(1) : "N/A"}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="form-group">
                        <label className="form-label">Description</label>
                        <div className="form-control-plaintext" style={{ whiteSpace: "pre-wrap" }}>
                          {selectedObservation.description || "-"}
                        </div>
                      </div>
                    </div>
                    {selectedObservation.improvement_proposal && (
                      <div className="col-12">
                        <div className="form-group">
                          <label className="form-label">Proposition d'amélioration</label>
                          <div className="form-control-plaintext" style={{ whiteSpace: "pre-wrap" }}>
                            {selectedObservation.improvement_proposal}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="nk-block nk-block-md mt-4">
                  <h6 className="text-soft mb-3">
                    <Icon name="user" className="me-2"></Icon>
                    Informations complémentaires
                  </h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Créé par</label>
                        <div className="form-control-plaintext">
                          {selectedObservation.creator
                            ? `${selectedObservation.creator.first_name || ""} ${selectedObservation.creator.last_name || ""}`.trim() || selectedObservation.creator.email
                            : "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Site</label>
                        <div className="form-control-plaintext">
                          {selectedObservation.site?.name || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Date de création</label>
                        <div className="form-control-plaintext">
                          {selectedObservation.created_at ? new Date(selectedObservation.created_at).toLocaleString("fr-FR") : "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="text-end mt-4">
              <Button color="light" onClick={() => setViewModal(false)}>
                Fermer
              </Button>
            </div>
          </ModalBody>
        </Modal>

        {/* Modal de confirmation pour sélectionner la carte du mois */}
        <Modal isOpen={confirmModal} toggle={onCancelSelect} className="modal-dialog-centered">
          <div className="modal-header">
            <h5 className="modal-title">Confirmer la sélection</h5>
            <button className="close" onClick={onCancelSelect}>
              <Icon name="cross"></Icon>
            </button>
          </div>
          <ModalBody>
            <div className="text-center">
              <div className="mb-3">
                <Icon name="alert-circle" className="text-warning" style={{ fontSize: "48px" }}></Icon>
              </div>
              <h6 className="mb-3">
                Êtes-vous sûr de vouloir sélectionner cette observation comme carte du mois ?
              </h6>
              {observationToSelect && (
                <div className="card bg-light mb-3">
                  <div className="card-body text-start">
                    <h6 className="mb-2">{observationToSelect.title}</h6>
                    <p className="text-soft mb-2" style={{ fontSize: "0.875rem" }}>
                      {observationToSelect.description?.substring(0, 150)}
                      {observationToSelect.description?.length > 150 ? "..." : ""}
                    </p>
                    <div className="d-flex gap-2 flex-wrap">
                      {getTypeBadge(observationToSelect.type)}
                      {getSeverityBadge(observationToSelect.severity)}
                      <Badge color="success">
                        Score: {observationToSelect.month_score ? parseFloat(observationToSelect.month_score).toFixed(1) : "N/A"}
                      </Badge>
                    </div>
                  </div>
                </div>
              )}
              <p className="text-soft small">
                Cette action remplacera la carte du mois actuellement sélectionnée (si elle existe).
              </p>
            </div>
            <div className="text-end mt-4">
              <Button color="light" onClick={onCancelSelect} className="me-2">
                Annuler
              </Button>
              <Button color="primary" onClick={onConfirmSelect} disabled={selectingCard}>
                {selectingCard ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Sélection en cours...
                  </>
                ) : (
                  "Confirmer"
                )}
              </Button>
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default CardOfMonthPage;
